import { useCallback, useEffect, useRef, useState } from 'react';
import message from 'antd/es/message';
import { persistCartChange, readStoredCart } from '../utils/cartStorage';

const COALESCE_MS = 250;

export default function useStoredCart({ compact = false } = {}) {
  const [cart, setState] = useState(readStoredCart);
  const current = useRef(cart);
  const written = useRef(cart);
  const timer = useRef(null);

  const write = useCallback(
    (previous, next) => {
      try {
        persistCartChange(previous, next, window.localStorage, { compact });
        written.current = next;
        return true;
      } catch (error) {
        console.error('Failed to persist cart change', error);
        message.error(
          'Promjena korpe nije sačuvana. Provjerite slobodan prostor na uređaju.',
        );
        return false;
      }
    },
    [compact],
  );

  const flush = useCallback(() => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    if (written.current !== current.current) {
      write(written.current, current.current);
    }
  }, [write]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      flush();
    };
  }, [flush]);

  const setCart = useCallback(
    (value) => {
      const next = typeof value === 'function' ? value(current.current) : value;
      if (next === current.current) return true;
      if (timer.current === null) {
        if (!write(current.current, next)) return false;
        current.current = next;
        setState(next);
        timer.current = setTimeout(() => {
          timer.current = null;
          if (written.current !== current.current) {
            write(written.current, current.current);
          }
        }, COALESCE_MS);
        return true;
      }
      current.current = next;
      setState(next);
      return true;
    },
    [write],
  );

  return [cart, setCart];
}
